import { gql, useMutation } from '@apollo/client';
import { IconButton, useDisclosure } from '@chakra-ui/react';
import * as React from 'react';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { logError } from '@/imports/logging/ClientLogger';
import { PinIcon } from '@/imports/ui/chakra/customIcon';

import {
  PinEventButton_UpdateConferenceEvent,
  PinEventButton_UpdateConferenceEventVariables,
} from './graphql-types/PinEventButton_UpdateConferenceEvent';
import {
  PinEventButton_UpdateSimpleEvent,
  PinEventButton_UpdateSimpleEventVariables,
} from './graphql-types/PinEventButton_UpdateSimpleEvent';

const UPDATE_EVENT_SIMPLE = gql`
  mutation PinEventButton_UpdateSimpleEvent($input: UpdateSimpleEventInput!) {
    updateSimpleEvent(input: $input) {
      result {
        _id
        pinned
      }
    }
  }
`;

const UPDATE_EVENT_CONFERENCE = gql`
  mutation PinEventButton_UpdateConferenceEvent(
    $input: UpdateConferenceEventInput!
  ) {
    updateConferenceEvent(input: $input) {
      result {
        _id
        pinned
      }
    }
  }
`;

export const PinEventButton: React.FC<{
  event: {
    _id: string;
    __typename: 'SimpleEventV2' | 'ConferenceEventV2';
    pinned?: boolean | null;
  };
}> = ({ event }) => {
  const confirmDialog = useDisclosure();
  const isPinned = !!event.pinned;

  const [updateSimpleEvent] = useMutation<
    PinEventButton_UpdateSimpleEvent,
    PinEventButton_UpdateSimpleEventVariables
  >(UPDATE_EVENT_SIMPLE);

  const [updateConferenceEvent] = useMutation<
    PinEventButton_UpdateConferenceEvent,
    PinEventButton_UpdateConferenceEventVariables
  >(UPDATE_EVENT_CONFERENCE);

  const onConfirm = async (): Promise<void> => {
    try {
      if (event.__typename === 'SimpleEventV2') {
        await updateSimpleEvent({
          variables: {
            input: {
              _id: event._id,
              data: {
                pinned: !isPinned,
              },
            },
          },
        });
      }

      if (event.__typename === 'ConferenceEventV2') {
        await updateConferenceEvent({
          variables: {
            input: {
              eventId: event._id,
              data: {
                pinned: !isPinned,
              },
            },
          },
        });
      }
    } catch (err) {
      logError(err);
    }
  };

  return (
    <>
      <IconButton
        aria-label={isPinned ? 'Pinned event' : 'Pin event'}
        icon={
          <PinIcon
            boxSize="6"
            fill={isPinned ? 'currentColor' : 'none'}
            color={isPinned ? 'primary.500' : 'gray.500'}
          />
        }
        size="md"
        variant="ghost"
        minW="10"
        h="10"
        p="2"
        position="relative"
        zIndex="1"
        flexShrink={0}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          confirmDialog.onOpen();
        }}
      />
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={confirmDialog.onClose}
        title={isPinned ? 'Unpin this Event' : 'Pin this Event'}
        body={
          isPinned
            ? 'This will unpin the event from the top of the Upcoming event list.'
            : 'This will pin the event to the top of the Upcoming event list.'
        }
        confirmButtonText={isPinned ? 'Unpin Event' : 'Pin Event'}
        onConfirm={onConfirm}
      />
    </>
  );
};
